import { Controller } from "@hotwired/stimulus"
import { select, pointer } from "d3-selection"
import { scaleTime, scaleLinear } from "d3-scale"
import { line as d3Line, area as d3Area, curveMonotoneX } from "d3-shape"
import { bisector } from "d3-array"
import { formatMonthDayTime, isDarkMode } from "../lib/helpers"
import { createTooltip, fillTooltip, hideTooltip, positionTooltip } from "../lib/chart_tooltip"
import { onMorph } from "../lib/reactor"
import { seriesPalette, referenceStroke } from "../lib/chart_series"
import { createReveal, reveal } from "../lib/chart_reveal"

const PADDING_Y = 4
const LABEL_WIDTH = 36
const GAP = 8
const bisectDate = bisector((d) => d.date).center

export default class extends Controller {
  static values = {
    points: Array,
    windowSeconds: Number,
    references: Array,
    domain: Array,
    series: { type: String, default: "astral" },
    label: { type: String, default: "" },
    decimals: { type: Number, default: 0 },
  }

  connect() {
    this.#reveal = createReveal()
    this.#group = this.element.closest("[data-sparkline-group]") || this.element
    this.#group.addEventListener("autoscaler-sparkline:scrub", this.#onScrub)
    document.addEventListener("theme:changed", this.#onThemeChange)
    this.#observeResize()
    onMorph(this)
  }

  disconnect() {
    this.#resizeObserver?.disconnect()
    document.removeEventListener("theme:changed", this.#onThemeChange)
    this.#group.removeEventListener("autoscaler-sparkline:scrub", this.#onScrub)
    this.#interaction = null
  }

  morph() {
    this.#render()
  }

  #resizeObserver
  #group
  // One hover drives every sparkline in the group. A null time clears it.
  #onScrub = ({ detail: { tMs } }) => {
    if (tMs == null) this.#interaction?.hide()
    else this.#scrubToTime(tMs)
  }
  #renderedWidth
  #reveal = null
  #interaction = null
  #onThemeChange = () => this.#render()

  #render() {
    this.element.innerHTML = ""
    this.element.style.position = "relative"
    this.#interaction = null
    // Scrub restore runs after the plot is rebuilt (end of #render).

    const data = this.pointsValue.map(([t, v]) => ({
      date: new Date(t * 1000),
      v,
    }))
    const references = this.referencesValue
    const domainOverride = this.domainValue
    const palette = seriesPalette(this.seriesValue)
    const dark = isDarkMode()

    // Prefer layout size. Fall back when utilities/layout are not ready yet
    // (empty first paint, or test browsers missing tailwind height classes).
    const rect = this.element.getBoundingClientRect()
    const width = rect.width || this.element.clientWidth || 0
    const height = rect.height || this.element.clientHeight || 112
    if (!width) return

    const svgWidth = Math.max(width - LABEL_WIDTH - GAP, 1)

    const present = data.filter((d) => d.v != null)
    if (present.length < 2) return

    // Configured domain (dyno min/max) is the baseline, but always expand to
    // fit live values so a quantity above maximum cannot paint outside the plot.
    const values = present.map((d) => d.v)
    let lo, hi
    if (domainOverride.length === 2) {
      ;[lo, hi] = domainOverride
      lo = Math.min(lo, ...values)
      hi = Math.max(hi, ...values)
    } else {
      const all = [...values, ...references]
      lo = Math.min(...all)
      hi = Math.max(...all)
    }
    if (lo === hi) {
      lo -= 1
      hi += 1
    }

    const now = Date.now()
    const xScale = scaleTime()
      .domain([
        new Date(now - this.windowSecondsValue * 1000),
        new Date(now),
      ])
      .range([0, svgWidth])

    const yScale = scaleLinear()
      .domain([lo, hi])
      .range([height - PADDING_Y, PADDING_Y])

    // Clip series inside the SVG so a domain miss never paints over the caption.
    // Keep the host free of overflow:hidden so the HTML tooltip can sit above the line.
    const svg = select(this.element)
      .append("svg")
      .attr("width", svgWidth)
      .attr("height", height)
      .attr("aria-hidden", "true")
      .style("flex", "1 1 0%")
      .style("min-width", "0")
      .style("overflow", "hidden")

    const clipId = `sparkline-clip-${crypto.randomUUID()}`
    svg
      .append("defs")
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("width", svgWidth)
      .attr("height", height)

    const plot = svg.append("g").attr("clip-path", `url(#${clipId})`)
    const series = plot.append("g")

    const areaShape = d3Area()
      .defined((d) => d.v != null)
      .x((d) => xScale(d.date))
      .y0(yScale(lo))
      .y1((d) => yScale(d.v))
      .curve(curveMonotoneX)

    series
      .append("path")
      .datum(data)
      .attr("fill", palette.band)
      .attr("d", areaShape)

    for (const v of references.filter((r) => r >= lo && r <= hi)) {
      plot
        .append("line")
        .attr("x1", 0)
        .attr("x2", svgWidth)
        .attr("y1", yScale(v))
        .attr("y2", yScale(v))
        .attr("stroke", referenceStroke(dark))
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2 3")
    }

    const lineShape = d3Line()
      .defined((d) => d.v != null)
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.v))
      .curve(curveMonotoneX)

    series
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", palette.stroke)
      .attr("stroke-width", 1.5)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", lineShape)

    reveal(series.node(), this.#reveal)

    const crosshair = plot
      .append("line")
      .attr("y1", 0)
      .attr("y2", height)
      .attr("stroke", "var(--color-hairline-strong)")
      .attr("stroke-width", 1)
      .attr("data-sparkline-crosshair", "")
      .style("display", "none")

    const focus = plot
      .append("circle")
      .attr("r", 3)
      .attr("fill", palette.stroke)
      .attr("stroke", "var(--color-card)")
      .attr("stroke-width", 1.5)
      .attr("data-sparkline-focus", "")
      .style("display", "none")

    const tooltip = createTooltip(this.element, {
      series: this.seriesValue === "azure" ? "azure" : "astral",
      compact: true,
      attributes: { "data-sparkline-tooltip": "" },
    })

    const showAt = (idx) => {
      const d = data[idx]
      if (!d || d.v == null) {
        hide()
        return
      }

      const xPos = xScale(d.date)
      const yPos = yScale(d.v)
      crosshair.style("display", null).attr("x1", xPos).attr("x2", xPos)
      focus.style("display", null).attr("cx", xPos).attr("cy", yPos)

      const value = this.decimalsValue > 0 ? Number(d.v).toFixed(this.decimalsValue) : String(d.v)
      fillTooltip(tooltip, { title: formatMonthDayTime(d.date), rows: [[this.labelValue, value]] })
      // Above the point, kept inside the plot.
      positionTooltip(tooltip, {
        x: xPos,
        width: svgWidth,
        top: (tipHeight) => {
          const above = Math.max(0, yPos - tipHeight - 6)
          return above + tipHeight > height ? Math.max(0, height - tipHeight) : above
        },
      })
    }

    const hide = () => {
      crosshair.style("display", "none")
      focus.style("display", "none")
      hideTooltip(tooltip)
    }

    this.#interaction = { data, xScale, showAt, hide }

    svg
      .append("rect")
      .attr("width", svgWidth)
      .attr("height", height)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .style("cursor", "crosshair")
      .on("mousemove", (event) => {
        const [mx] = pointer(event)
        const tMs = xScale.invert(mx).getTime()
        this.#broadcastScrub(tMs)
      })
      .on("mouseleave", (event) => this.#broadcastHide(event))

    const labels = document.createElement("div")
    labels.style.cssText = `
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: ${LABEL_WIDTH}px;
      flex-shrink: 0;
      text-align: right;
      font-size: 10px;
      line-height: 1;
      font-variant-numeric: tabular-nums;
      color: var(--color-ink-muted, var(--color-gray-400));
    `
    const hiSpan = document.createElement("span")
    hiSpan.textContent = this.#formatBand(hi)
    const loSpan = document.createElement("span")
    loSpan.textContent = this.#formatBand(lo)
    labels.append(hiSpan, loSpan)
    this.element.appendChild(labels)

    this.#renderedWidth = width

    // Resize / theme / morph wipe the SVG. Re-apply scrub while the pointer
    // is still over the pair (History charts restore the same way).
    this.#restoreScrub()
  }

  #broadcastScrub(tMs) {
    if (this.#group !== this.element) this.#group.dataset.sparklineScrubT = String(tMs)
    this.dispatch("scrub", { target: this.#group, detail: { tMs }, bubbles: false })
  }

  #broadcastHide(event) {
    // Stay scrubbed when the pointer moves to the sibling plot or the group gap.
    // Use relatedTarget (not :hover) so synthetic leave with null relatedTarget clears.
    const related = event?.relatedTarget
    if (this.#group !== this.element && related instanceof Node && this.#group.contains(related)) return

    delete this.#group.dataset.sparklineScrubT
    this.dispatch("scrub", { target: this.#group, detail: { tMs: null }, bubbles: false })
  }

  #restoreScrub() {
    const tMs = this.#group.dataset.sparklineScrubT
    if (tMs == null) return
    // Only restore while the pointer is still over this pair.
    if (!this.#group.matches(":hover")) return
    this.#scrubToTime(Number(tMs))
  }

  #scrubToTime(tMs) {
    const interaction = this.#interaction
    if (!interaction) return

    const idx = bisectDate(interaction.data, new Date(tMs))
    interaction.showAt(idx)
  }

  // Up to the unit's decimals without trailing zeros, so "300" still fits LABEL_WIDTH.
  #formatBand(value) {
    return String(Number(value.toFixed(this.decimalsValue)))
  }

  #observeResize() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === this.#renderedWidth) return
      this.#render()
    })
    this.#resizeObserver.observe(this.element)
  }
}
